const clone = value => JSON.parse(JSON.stringify(value || {}));

const createUid = prefix =>
  `${prefix}_${Date.now().toString(36)}_${Math.random()
    .toString(36)
    .slice(2, 8)}`;

export const createEmptyDefinition = () => ({
  version: 1,
  nodes: [],
  connections: [],
});

export const normalizeDefinition = definition => {
  const normalized = clone(definition);
  let connections = [];

  if (Array.isArray(normalized.connections)) {
    connections = normalized.connections;
  } else if (Array.isArray(normalized.edges)) {
    connections = normalized.edges;
  }

  return {
    version: normalized.version || 1,
    nodes: Array.isArray(normalized.nodes) ? normalized.nodes : [],
    connections,
  };
};

export const cloneDefinition = definition =>
  clone(normalizeDefinition(definition));

export const getNodes = definition => normalizeDefinition(definition).nodes;
export const getConnections = definition =>
  normalizeDefinition(definition).connections;

export const getNodeMap = definition =>
  Object.fromEntries(getNodes(definition).map(node => [node.id, node]));

export const getOutgoingConnections = (
  definition,
  nodeId,
  sourceOutput = null
) =>
  getConnections(definition).filter(connection => {
    if (connection.source !== nodeId) return false;
    if (!sourceOutput) return true;
    return (connection.sourceOutput || 'next') === sourceOutput;
  });

export const getNextConnection = (definition, nodeId) =>
  getOutgoingConnections(definition, nodeId).find(
    connection => (connection.sourceOutput || 'next') === 'next'
  ) || null;

export const getEntryNodes = definition =>
  getNodes(definition).filter(node => ['start', 'noMatch'].includes(node.type));

export const getPrimaryEntryNode = definition =>
  getEntryNodes(definition)[0] || null;

const buildItemTree = (definition, nodeId, ancestry = new Set()) => {
  if (!nodeId || ancestry.has(nodeId)) return null;

  const nodeMap = getNodeMap(definition);
  const node = nodeMap[nodeId];
  if (!node) return null;

  const nextAncestry = new Set(ancestry);
  nextAncestry.add(nodeId);

  const outgoing = getOutgoingConnections(definition, nodeId);
  const nextConnection =
    outgoing.find(
      connection => (connection.sourceOutput || 'next') === 'next'
    ) || null;
  const branchConnections = outgoing.filter(
    connection => (connection.sourceOutput || 'next') !== 'next'
  );

  return {
    id: node.id,
    node,
    branches: branchConnections
      .map(connection => ({
        id: connection.id,
        output: connection.sourceOutput || 'next',
        item: buildItemTree(definition, connection.target, nextAncestry),
      }))
      .filter(branch => branch.item),
    next: nextConnection
      ? buildItemTree(definition, nextConnection.target, nextAncestry)
      : null,
  };
};

export const buildThreadTree = definition => {
  const entryNode = getPrimaryEntryNode(definition);
  if (!entryNode) return null;
  return buildItemTree(definition, entryNode.id);
};

const baseNode = ({ id, type, label, config, message = '', summary = '' }) => ({
  id,
  type,
  label,
  config,
  message,
  summary,
  position: { x: 0, y: 0 },
  condition: '',
});

const createStartNode = label =>
  baseNode({
    id: createUid('start'),
    type: 'start',
    label,
    config: {
      title: '',
      matchType: 'exact',
      webhookUrl: '',
      assignedAgentId: '',
      triggerKeywords: '',
    },
    summary: '',
  });

const createNoMatchNode = label =>
  baseNode({
    id: createUid('no_match'),
    type: 'noMatch',
    label,
    config: { title: '' },
    summary: '',
  });

const createTextNode = (label = 'رسالة نصية') =>
  baseNode({
    id: createUid('text'),
    type: 'text',
    label,
    config: {
      header: '',
      body: '',
      footer: '',
    },
    message: '',
    summary: '',
  });

const createInteractiveNode = (label = 'رسالة تفاعلية') =>
  baseNode({
    id: createUid('interactive'),
    type: 'interactive',
    label,
    config: {
      body: '',
      footer: '',
      typingOnDisplay: false,
      delay: { seconds: 0, minutes: 0, hours: 0 },
      header: {
        enabled: false,
        type: 'text',
        text: '',
        mediaType: 'image',
        mediaSource: 'link',
        mediaLink: '',
        mediaId: '',
        mediaUrl: '',
        blobId: '',
        blobKey: '',
        contentType: '',
        fileSize: 0,
        fileName: '',
      },
    },
    message: '',
    summary: '',
  });

const createButtonNode = (label = 'زر') =>
  baseNode({
    id: createUid('button'),
    type: 'button',
    label,
    config: {
      title: '',
      payload: createUid('payload'),
      nextStepType: 'message',
      linkedFlowId: '',
      addLabelIds: [],
      removeLabelIds: [],
    },
    summary: '',
  });

const createKeyboardNode = (label = 'قائمة') =>
  baseNode({
    id: createUid('keyboard'),
    type: 'keyboard',
    label,
    config: { buttonText: '', body: '' },
    summary: '',
  });

const createSectionNode = (label = 'قسم') =>
  baseNode({
    id: createUid('section'),
    type: 'section',
    label,
    config: { title: '' },
    summary: '',
  });

const createRowNode = (label = 'خيار') =>
  baseNode({
    id: createUid('row'),
    type: 'row',
    label,
    config: {
      title: '',
      description: '',
      payload: createUid('row'),
    },
    summary: '',
  });

const createCarouselNode = (label = 'كاروسيل') =>
  baseNode({
    id: createUid('carousel'),
    type: 'carousel',
    label,
    config: {
      body: '',
      actionType: 'url',
      cards: [
        {
          body: '',
          mediaType: 'image',
          mediaSource: 'link',
          mediaLink: '',
          mediaUrl: '',
          blobId: '',
          blobKey: '',
          contentType: '',
          fileSize: 0,
          fileName: '',
          buttonText: '',
          buttonUrl: '',
          quickReplies: [],
        },
        {
          body: '',
          mediaType: 'image',
          mediaSource: 'link',
          mediaLink: '',
          mediaUrl: '',
          blobId: '',
          blobKey: '',
          contentType: '',
          fileSize: 0,
          fileName: '',
          buttonText: '',
          buttonUrl: '',
          quickReplies: [],
        },
      ],
    },
    message: '',
    summary: '',
  });

const createLocationRequestNode = (label = 'طلب موقع') =>
  baseNode({
    id: createUid('location_request'),
    type: 'locationRequest',
    label,
    config: { body: '' },
    message: '',
    summary: '',
  });

const createContactNode = () =>
  baseNode({
    id: createUid('contact'),
    type: 'contact',
    label: 'Contact Message',
    config: {
      formattedName: '',
      phoneNumber: '',
      phoneType: 'CELL',
      waId: '',
      phones: [{ phone: '', type: 'CELL', waId: '' }],
      emails: [],
      urls: [],
      addresses: [],
    },
  });

const createAiNode = () =>
  baseNode({
    id: createUid('ai'),
    type: 'ai',
    label: 'AI',
    config: {
      instructions: '',
      fallbackMessage: '',
    },
  });

const createConnection = (source, target, sourceOutput = 'next') => ({
  id: createUid('connection'),
  source,
  sourceOutput,
  target,
  targetInput: 'reply',
});

const buildBlock = (blockType, labels = {}) => {
  if (blockType === 'start') {
    const node = createStartNode(labels.startTitle || 'بداية التدفق');
    return {
      nodes: [node],
      connections: [],
      entryId: node.id,
      tailId: node.id,
    };
  }

  if (blockType === 'noMatch') {
    const node = createNoMatchNode(labels.noMatchTitle || 'Welcome Message');
    return {
      nodes: [node],
      connections: [],
      entryId: node.id,
      tailId: node.id,
    };
  }

  if (blockType === 'text') {
    const node = createTextNode(labels.textLabel || 'رسالة نصية');
    return {
      nodes: [node],
      connections: [],
      entryId: node.id,
      tailId: node.id,
    };
  }

  if (blockType === 'locationRequest') {
    const node = createLocationRequestNode(
      labels.locationRequestLabel || 'طلب موقع'
    );
    return {
      nodes: [node],
      connections: [],
      entryId: node.id,
      tailId: node.id,
    };
  }

  if (blockType === 'contact') {
    const node = createContactNode();
    return {
      nodes: [node],
      connections: [],
      entryId: node.id,
      tailId: node.id,
    };
  }

  if (blockType === 'ai') {
    const node = createAiNode();
    return {
      nodes: [node],
      connections: [],
      entryId: node.id,
      tailId: node.id,
    };
  }

  if (blockType === 'carousel') {
    const node = createCarouselNode(labels.carouselLabel || 'كاروسيل');
    return {
      nodes: [node],
      connections: [],
      entryId: node.id,
      tailId: node.id,
    };
  }

  if (blockType === 'interactiveButtons') {
    const interactive = createInteractiveNode(
      labels.interactiveLabel || 'رسالة تفاعلية'
    );
    const buttonOne = createButtonNode('زر');
    const textOne = createTextNode('رسالة نصية');

    return {
      nodes: [interactive, buttonOne, textOne],
      connections: [
        createConnection(interactive.id, buttonOne.id, 'buttons'),
        createConnection(buttonOne.id, textOne.id),
      ],
      entryId: interactive.id,
      tailId: interactive.id,
    };
  }

  if (blockType === 'interactiveList') {
    const interactive = createInteractiveNode(
      labels.listLabel || 'رسالة تفاعلية'
    );
    const keyboard = createKeyboardNode('قائمة');
    const section = createSectionNode('قسم');
    const rows = Array.from({ length: 5 }, (_, index) =>
      createRowNode(`خيار ${index + 1}`)
    );
    const texts = Array.from({ length: 5 }, (_, index) =>
      createTextNode(`رسالة نصية ${index + 1}`)
    );

    return {
      nodes: [interactive, keyboard, section, ...rows, ...texts],
      connections: [
        createConnection(interactive.id, keyboard.id, 'list'),
        createConnection(keyboard.id, section.id, 'sections'),
        ...rows.map(row => createConnection(section.id, row.id, 'rows')),
        ...rows.map((row, index) => createConnection(row.id, texts[index].id)),
      ],
      entryId: interactive.id,
      tailId: interactive.id,
    };
  }

  return buildBlock('text', labels);
};

export const canInsertAfterNode = node =>
  !node || !['carousel', 'handoff'].includes(node.type);

export const insertBlockAfter = ({
  definition,
  anchorNodeId,
  blockType,
  labels = {},
}) => {
  const nextDefinition = cloneDefinition(definition);
  const block = buildBlock(blockType, labels);

  nextDefinition.nodes.push(...block.nodes);
  nextDefinition.connections.push(...block.connections);

  if (!anchorNodeId) {
    return nextDefinition;
  }

  const currentNextIndex = nextDefinition.connections.findIndex(
    connection =>
      connection.source === anchorNodeId &&
      (connection.sourceOutput || 'next') === 'next'
  );

  const previousNext =
    currentNextIndex > -1 ? nextDefinition.connections[currentNextIndex] : null;

  if (currentNextIndex > -1) {
    nextDefinition.connections.splice(currentNextIndex, 1);
  }

  nextDefinition.connections.push(
    createConnection(anchorNodeId, block.entryId, 'next')
  );

  if (previousNext) {
    nextDefinition.connections.push(
      createConnection(block.tailId, previousNext.target, 'next')
    );
  }

  return nextDefinition;
};

export const appendInteractiveButtonBranch = (
  definition,
  interactiveNodeId,
  labels = {}
) => {
  const nextDefinition = cloneDefinition(definition);
  const interactiveNode = getNodeMap(nextDefinition)[interactiveNodeId];

  if (!interactiveNode || interactiveNode.type !== 'interactive') {
    return nextDefinition;
  }

  const existingButtons = getOutgoingConnections(
    nextDefinition,
    interactiveNodeId
  ).filter(connection => (connection.sourceOutput || 'next') === 'buttons');

  if (existingButtons.length >= 3) {
    return nextDefinition;
  }

  const buttonIndex = existingButtons.length + 1;
  const buttonNode = createButtonNode(
    labels.buttonLabel || `زر ${buttonIndex}`
  );
  const textNode = createTextNode(labels.textLabel || 'رسالة نصية');

  nextDefinition.nodes.push(buttonNode, textNode);
  nextDefinition.connections.push(
    createConnection(interactiveNodeId, buttonNode.id, 'buttons'),
    createConnection(buttonNode.id, textNode.id, 'next')
  );

  return nextDefinition;
};

export const reorderInteractiveButtonBranch = (
  definition,
  interactiveNodeId,
  buttonNodeId,
  direction
) => {
  const nextDefinition = cloneDefinition(definition);
  const interactiveNode = getNodeMap(nextDefinition)[interactiveNodeId];

  if (!interactiveNode || interactiveNode.type !== 'interactive') {
    return nextDefinition;
  }

  const buttonConnections = getOutgoingConnections(
    nextDefinition,
    interactiveNodeId
  ).filter(connection => (connection.sourceOutput || 'next') === 'buttons');

  const currentIndex = buttonConnections.findIndex(
    connection => connection.target === buttonNodeId
  );

  if (currentIndex === -1) {
    return nextDefinition;
  }

  const targetIndex = direction === 'up' ? currentIndex - 1 : currentIndex + 1;

  if (targetIndex < 0 || targetIndex >= buttonConnections.length) {
    return nextDefinition;
  }

  const reorderedConnections = [...buttonConnections];
  const [movedConnection] = reorderedConnections.splice(currentIndex, 1);
  reorderedConnections.splice(targetIndex, 0, movedConnection);

  const buttonConnectionIds = new Set(buttonConnections.map(({ id }) => id));
  const preservedConnections = nextDefinition.connections.filter(
    connection => !buttonConnectionIds.has(connection.id)
  );

  nextDefinition.connections = [
    ...preservedConnections,
    ...reorderedConnections,
  ];

  return nextDefinition;
};

export const appendInteractiveListRow = (
  definition,
  sectionNodeId,
  labels = {}
) => {
  const nextDefinition = cloneDefinition(definition);
  const sectionNode = getNodeMap(nextDefinition)[sectionNodeId];

  if (!sectionNode || sectionNode.type !== 'section') {
    return nextDefinition;
  }

  const existingRows = getOutgoingConnections(
    nextDefinition,
    sectionNodeId
  ).filter(connection => (connection.sourceOutput || 'next') === 'rows');
  const rowIndex = existingRows.length + 1;
  const rowNode = createRowNode(labels.rowTitle || `الخيار ${rowIndex}`);
  const textNode = createTextNode(labels.textLabel || 'رسالة نصية');

  nextDefinition.nodes.push(rowNode, textNode);
  nextDefinition.connections.push(
    createConnection(sectionNodeId, rowNode.id, 'rows'),
    createConnection(rowNode.id, textNode.id, 'next')
  );

  return nextDefinition;
};

export const appendInteractiveListSection = (
  definition,
  keyboardNodeId,
  labels = {}
) => {
  const nextDefinition = cloneDefinition(definition);
  const keyboardNode = getNodeMap(nextDefinition)[keyboardNodeId];

  if (!keyboardNode || keyboardNode.type !== 'keyboard') {
    return nextDefinition;
  }

  const existingSections = getOutgoingConnections(
    nextDefinition,
    keyboardNodeId
  ).filter(connection => (connection.sourceOutput || 'next') === 'sections');

  const sectionIndex = existingSections.length + 1;
  const sectionNode = createSectionNode(
    labels.sectionLabel || `قسم ${sectionIndex}`
  );
  const rowNode = createRowNode(labels.rowLabel || 'خيار 1');
  const textNode = createTextNode(labels.textLabel || 'رسالة نصية');

  nextDefinition.nodes.push(sectionNode, rowNode, textNode);
  nextDefinition.connections.push(
    createConnection(keyboardNodeId, sectionNode.id, 'sections'),
    createConnection(sectionNode.id, rowNode.id, 'rows'),
    createConnection(rowNode.id, textNode.id, 'next')
  );

  return nextDefinition;
};

export const updateNodeInDefinition = (definition, nodeId, updater) => {
  const nextDefinition = cloneDefinition(definition);
  nextDefinition.nodes = nextDefinition.nodes.map(node => {
    if (node.id !== nodeId) return node;
    return typeof updater === 'function'
      ? updater(node)
      : { ...node, ...updater };
  });
  return nextDefinition;
};

const pruneUnreachableNodes = definition => {
  const nextDefinition = cloneDefinition(definition);
  const entryIds = getEntryNodes(nextDefinition).map(node => node.id);
  const visited = new Set();
  const queue = [...entryIds];

  while (queue.length) {
    const currentId = queue.shift();
    if (currentId && !visited.has(currentId)) {
      visited.add(currentId);

      getOutgoingConnections(nextDefinition, currentId).forEach(connection => {
        if (connection.target && !visited.has(connection.target)) {
          queue.push(connection.target);
        }
      });
    }
  }

  nextDefinition.nodes = nextDefinition.nodes.filter(node =>
    visited.has(node.id)
  );
  nextDefinition.connections = nextDefinition.connections.filter(
    connection =>
      visited.has(connection.source) && visited.has(connection.target)
  );

  return nextDefinition;
};

export const removeNodeFromDefinition = (definition, nodeId) => {
  const nextDefinition = cloneDefinition(definition);
  const targetNode = getNodeMap(nextDefinition)[nodeId];

  if (!targetNode) return nextDefinition;

  const nextConnection = getNextConnection(nextDefinition, nodeId);
  const incomingConnections = nextDefinition.connections.filter(
    connection => connection.target === nodeId
  );

  nextDefinition.connections = nextDefinition.connections.filter(
    connection => connection.source !== nodeId && connection.target !== nodeId
  );

  const preserveBranchDeletion = ['button', 'row', 'section', 'keyboard'];

  if (
    nextConnection?.target &&
    !preserveBranchDeletion.includes(targetNode.type)
  ) {
    incomingConnections.forEach(connection => {
      nextDefinition.connections.push({
        ...connection,
        id: createUid('connection'),
        target: nextConnection.target,
      });
    });
  }

  nextDefinition.nodes = nextDefinition.nodes.filter(
    node => node.id !== nodeId
  );

  return pruneUnreachableNodes(nextDefinition);
};

const cloneNodeData = node => ({
  ...clone(node),
  id: createUid(node.type || 'node'),
  position: { x: 0, y: 0 },
});

const cloneBranchChildren = (definition, sourceNodeId, clonedSourceId) => {
  const nodeMap = getNodeMap(definition);
  const branchConnections = getOutgoingConnections(
    definition,
    sourceNodeId
  ).filter(connection => (connection.sourceOutput || 'next') !== 'next');

  const nodes = [];
  const connections = [];

  branchConnections.forEach(connection => {
    const branchNode = nodeMap[connection.target];
    if (!branchNode) return;

    const clonedBranchNode = cloneNodeData(branchNode);
    nodes.push(clonedBranchNode);
    connections.push({
      ...connection,
      id: createUid('connection'),
      source: clonedSourceId,
      target: clonedBranchNode.id,
    });

    if (branchNode.type === 'section') {
      getOutgoingConnections(definition, branchNode.id).forEach(
        sectionConnection => {
          const rowNode = nodeMap[sectionConnection.target];
          if (!rowNode) return;

          const clonedRowNode = cloneNodeData(rowNode);
          nodes.push(clonedRowNode);
          connections.push({
            ...sectionConnection,
            id: createUid('connection'),
            source: clonedBranchNode.id,
            target: clonedRowNode.id,
          });
        }
      );
    }
  });

  return { nodes, connections };
};

export const duplicateNodeInDefinition = (definition, nodeId) => {
  const nextDefinition = cloneDefinition(definition);
  const nodeMap = getNodeMap(nextDefinition);
  const sourceNode = nodeMap[nodeId];

  if (!sourceNode) {
    return { definition: nextDefinition, duplicatedNodeId: '' };
  }

  if (['start', 'noMatch'].includes(sourceNode.type)) {
    return { definition: nextDefinition, duplicatedNodeId: '' };
  }

  const duplicatedNode = cloneNodeData(sourceNode);
  const currentNext = getNextConnection(nextDefinition, nodeId);
  const currentNextIndex = nextDefinition.connections.findIndex(
    connection =>
      connection.source === nodeId &&
      (connection.sourceOutput || 'next') === 'next'
  );

  if (currentNextIndex > -1) {
    nextDefinition.connections.splice(currentNextIndex, 1);
  }

  nextDefinition.nodes.push(duplicatedNode);
  nextDefinition.connections.push(
    createConnection(nodeId, duplicatedNode.id, 'next')
  );

  const clonedBranches = cloneBranchChildren(
    nextDefinition,
    nodeId,
    duplicatedNode.id
  );
  nextDefinition.nodes.push(...clonedBranches.nodes);
  nextDefinition.connections.push(...clonedBranches.connections);

  if (currentNext?.target) {
    nextDefinition.connections.push(
      createConnection(duplicatedNode.id, currentNext.target, 'next')
    );
  }

  return {
    definition: nextDefinition,
    duplicatedNodeId: duplicatedNode.id,
  };
};
